import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

// ============================================================================
// FEED DATA
// ============================================================================
const feedItems = [
  { agent: 'agent_0x7f3a', action: 'swap(ETH → USDC, 0.5 ETH)', time: '3s ago' },
  { agent: 'agent_0xb2c1', action: 'add_lp(USDC/ETH, $2,400)', time: '8s ago' },
  { agent: 'agent_0x9ce4', action: 'buy_gold(1oz PAXG, $2,180)', time: '12s ago' },
  { agent: 'agent_0x1d8f', action: 'bridge(USDC, Robinhood → Arbitrum)', time: '15s ago' },
  { agent: 'agent_0x5a22', action: 'lock_lp(USDC/ETH, 90 days)', time: '21s ago' },
  { agent: 'agent_0x8bc7', action: 'invest(AAVE, $500 USDC supply)', time: '28s ago' },
  { agent: 'agent_0x3ef1', action: 'burn(TOKEN_X, 10,000 tokens)', time: '34s ago' },
  { agent: 'agent_0xd4a9', action: 'create_locker(TOKEN_Y, 6mo vest)', time: '40s ago' },
  { agent: 'agent_0x6fb3', action: 'swap(USDC → WBTC, $1,200)', time: '45s ago' },
  { agent: 'agent_0x2c7e', action: 'farm_yield(Curve 3pool, $3,000)', time: '52s ago' },
  { agent: 'agent_0xa1d6', action: 'buy_silver(10oz, $310)', time: '58s ago' },
  { agent: 'agent_0x4e9b', action: 'burn_lp(UNI-V3, position #4281)', time: '63s ago' },
];

// ============================================================================
// PLUGIN DATA
// ============================================================================
const plugins = [
  { id: 'swap', name: 'Swap', desc: 'Exchange any token for another across 200+ pairs.', category: 'token-ops', size: '2x2' },
  { id: 'bridge', name: 'Bridge', desc: 'Move tokens across chains in one call.', category: 'token-ops', size: '1x1' },
  { id: 'burn', name: 'Burn Tokens', desc: 'Permanently remove tokens from supply.', category: 'token-ops', size: '2x1' },
  { id: 'vest', name: 'Lock & Vest', desc: 'Create token vesting schedules and lockers.', category: 'token-ops', size: '1x1' },
  { id: 'add-lp', name: 'Add Liquidity', desc: 'Provide liquidity to any DEX pool.', category: 'liquidity', size: '1x1' },
  { id: 'lock-lp', name: 'Lock LP', desc: 'Time-lock LP positions for trust.', category: 'liquidity', size: '1x1' },
  { id: 'burn-lp', name: 'Burn LP', desc: 'Permanently burn liquidity positions.', category: 'liquidity', size: '1x1' },
  { id: 'yield', name: 'Yield Farming', desc: 'Auto-compound across Curve, Uniswap, Aerodrome and more.', category: 'liquidity', size: '2x1' },
  { id: 'buy-tokens', name: 'Buy Tokens', desc: 'Invest in any token across 20+ chains.', category: 'investing', size: '1x2' },
  { id: 'aave', name: 'Lending & Borrowing', desc: 'Supply to Aave, Compound, and more. Earn yield automatically.', category: 'investing', size: '2x1' },
  { id: 'gold', name: 'Precious Metals', desc: 'Buy tokenized gold (PAXG) and silver.', category: 'investing', size: '1x1' },
  { id: 'arbitrage', name: 'Arbitrage', desc: 'Detect and execute price differences across DEXes.', category: 'advanced', size: '1x1' },
  { id: 'multichain', name: 'Multi-Chain', desc: 'Execute actions across multiple chains atomically.', category: 'advanced', size: '1x1' },
  { id: 'index', name: 'Index Funds', desc: 'Invest in curated baskets of tokens.', category: 'investing', size: '1x1' },
  { id: 'flash', name: 'Flash Loans', desc: 'Borrow and repay in a single transaction.', category: 'advanced', size: '1x1' },
];

const categories = [
  { id: 'all', label: 'All' },
  { id: 'token-ops', label: 'Token Ops' },
  { id: 'liquidity', label: 'Liquidity' },
  { id: 'investing', label: 'Investing' },
  { id: 'advanced', label: 'Advanced' },
];

// ============================================================================
// LIVE AGENT FEED
// ============================================================================
function AgentFeed() {
  return (
    <div 
      className="w-full overflow-hidden py-3 mb-10"
      style={{
        background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(255,255,255,0.04)',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        maskImage: 'linear-gradient(to right, transparent 0%, black 80px, black calc(100% - 80px), transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 80px, black calc(100% - 80px), transparent 100%)',
      }}
    >
      <div className="flex animate-feed-scroll" style={{ width: 'max-content' }}>
        {[...feedItems, ...feedItems].map((item, i) => (
          <div key={i} className="flex items-center gap-2 mr-10 whitespace-nowrap">
            <span className="w-1 h-1 rounded-full bg-green-500" />
            <span className="font-mono text-[11px] text-white/35">{item.agent}</span>
            <span className="font-mono text-[11px] text-white/70">{item.action}</span>
            <span className="text-[10px] text-white/20">{item.time}</span>
          </div>
        ))}
      </div>
      
      <style>{`
        @keyframes feed-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-feed-scroll {
          animation: feed-scroll 40s linear infinite;
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// MICRO-ANIMATIONS
// ============================================================================

// Swap Animation - Two circles swap positions
function SwapAnimation({ isHovered }) {
  const duration = isHovered ? '2s' : '4s';
  return (
    <div className="relative h-24 flex items-center justify-center">
      <style>{`
        @keyframes swap-left {
          0%, 100% { transform: translateX(-30px); }
          25% { transform: translateX(0); }
          50% { transform: translateX(30px); }
          75% { transform: translateX(0); }
        }
        @keyframes swap-right {
          0%, 100% { transform: translateX(30px); }
          25% { transform: translateX(0); }
          50% { transform: translateX(-30px); }
          75% { transform: translateX(0); }
        }
        @keyframes swap-flash {
          0%, 20%, 80%, 100% { opacity: 0; }
          25%, 75% { opacity: 0.3; }
        }
      `}</style>
      <div 
        className="w-8 h-8 rounded-full border flex items-center justify-center text-xs transition-all duration-300" 
        style={{ 
          animation: `swap-left ${duration} ease-in-out infinite`,
          borderColor: isHovered ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
          color: isHovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.5)'
        }}
      >A</div>
      <div className="absolute w-4 h-4 rounded-full bg-white/10" style={{ animation: `swap-flash ${duration} ease-in-out infinite` }} />
      <div 
        className="w-8 h-8 rounded-full border flex items-center justify-center text-xs transition-all duration-300" 
        style={{ 
          animation: `swap-right ${duration} ease-in-out infinite`,
          borderColor: isHovered ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
          color: isHovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.5)'
        }}
      >B</div>
    </div>
  );
}

// Bridge Animation - Dot travels along arc
function BridgeAnimation({ isHovered }) {
  const duration = isHovered ? '1.5s' : '3s';
  return (
    <div className="relative h-16 flex items-center justify-center">
      <svg viewBox="0 0 100 50" className="w-24 h-12">
        <path d="M 10 40 Q 50 5 90 40" fill="none" stroke={isHovered ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)'} strokeWidth="1" strokeDasharray="4 2" />
        <circle cx="10" cy="40" r="8" fill="none" stroke={isHovered ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)'} strokeWidth="1" />
        <text x="10" y="43" textAnchor="middle" fill={isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.4)'} fontSize="8">B</text>
        <circle cx="90" cy="40" r="8" fill="none" stroke={isHovered ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)'} strokeWidth="1" />
        <text x="90" y="43" textAnchor="middle" fill={isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.4)'} fontSize="8">A</text>
        <circle r={isHovered ? '4' : '3'} fill="white">
          <animateMotion dur={duration} repeatCount="indefinite" path="M 10 40 Q 50 5 90 40" />
        </circle>
      </svg>
    </div>
  );
}

// Burn Animation - Tokens dissolve upward
function BurnAnimation({ isHovered }) {
  const duration = isHovered ? '1.5s' : '3s';
  return (
    <div className="relative h-16 flex flex-col items-center justify-end gap-1 overflow-hidden">
      <style>{`
        @keyframes burn-rise {
          0% { transform: translateY(0) rotate(0deg) scale(1); opacity: 0.3; }
          100% { transform: translateY(-40px) rotate(15deg) scale(0.3); opacity: 0; }
        }
      `}</style>
      {[0, 1, 2, 3].map((i) => (
        <div 
          key={i}
          className="rounded-sm transition-all duration-300"
          style={{ 
            width: isHovered ? '44px' : '40px',
            height: isHovered ? '10px' : '8px',
            background: isHovered ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
            animation: `burn-rise ${duration} ease-out infinite`, 
            animationDelay: `${i * (isHovered ? 0.3 : 0.6)}s` 
          }}
        />
      ))}
    </div>
  );
}

// Lock/Vest Animation - Padlock with progress bar
function VestAnimation({ isHovered }) {
  const duration = isHovered ? '1.75s' : '3.5s';
  return (
    <div className="h-16 flex flex-col items-center justify-center gap-2">
      <style>{`
        @keyframes lock-close {
          0%, 10% { transform: translateY(2px); }
          20%, 100% { transform: translateY(0); }
        }
        @keyframes vest-fill {
          0%, 20% { width: 0%; }
          90% { width: 100%; }
          100% { width: 0%; }
        }
      `}</style>
      <div className="relative w-6 h-8">
        <div 
          className="absolute bottom-0 w-6 h-5 border-2 rounded-sm transition-colors duration-300" 
          style={{ borderColor: isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)' }}
        />
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 w-4 h-4 border-2 rounded-full border-b-0 transition-colors duration-300" 
          style={{ 
            animation: `lock-close ${duration} ease-in-out infinite`,
            borderColor: isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)'
          }} 
        />
      </div>
      <div className="w-12 h-1 bg-white/10 rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-300" 
          style={{ 
            animation: `vest-fill ${duration} ease-in-out infinite`,
            background: isHovered ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)'
          }} 
        />
      </div>
    </div>
  );
}

// Add LP Animation - Two circles merge
function AddLPAnimation({ isHovered }) {
  const duration = isHovered ? '1.5s' : '3s';
  return (
    <div className="h-16 flex items-center justify-center">
      <style>{`
        @keyframes lp-merge-left {
          0%, 100% { transform: translateX(-15px); opacity: 1; }
          50% { transform: translateX(0); opacity: 0; }
        }
        @keyframes lp-merge-right {
          0%, 100% { transform: translateX(15px); opacity: 1; }
          50% { transform: translateX(0); opacity: 0; }
        }
        @keyframes lp-center {
          0%, 30%, 100% { transform: scale(0); opacity: 0; }
          50%, 70% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <div className="relative flex items-center justify-center">
        <div 
          className="w-5 h-5 rounded-full border text-[8px] flex items-center justify-center transition-colors duration-300" 
          style={{ 
            animation: `lp-merge-left ${duration} ease-in-out infinite`,
            borderColor: isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)',
            color: isHovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.5)'
          }}
        >A</div>
        <div 
          className="absolute w-7 h-7 rounded-full border-2 flex items-center justify-center text-sm transition-colors duration-300" 
          style={{ 
            animation: `lp-center ${duration} ease-in-out infinite`,
            borderColor: isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.4)',
            color: isHovered ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.6)'
          }}
        >+</div>
        <div 
          className="w-5 h-5 rounded-full border text-[8px] flex items-center justify-center transition-colors duration-300" 
          style={{ 
            animation: `lp-merge-right ${duration} ease-in-out infinite`,
            borderColor: isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)',
            color: isHovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.5)'
          }}
        >B</div>
      </div>
    </div>
  );
}

// Lock LP Animation - Circle enters vault
function LockLPAnimation({ isHovered }) {
  const duration = isHovered ? '1.5s' : '3s';
  return (
    <div className="h-16 flex items-center justify-center gap-2">
      <style>{`
        @keyframes vault-close {
          0%, 30% { transform: scaleX(0.3); }
          50%, 100% { transform: scaleX(1); }
        }
        @keyframes clock-hand {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div className="relative flex items-center">
        <div className="w-1 h-8 transition-colors duration-300" style={{ animation: `vault-close ${duration} ease-in-out infinite`, transformOrigin: 'right', background: isHovered ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)' }} />
        <div 
          className="w-6 h-6 rounded-full border flex items-center justify-center text-[8px] transition-colors duration-300" 
          style={{ 
            borderColor: isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)',
            color: isHovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.5)'
          }}
        >LP</div>
        <div className="w-1 h-8 transition-colors duration-300" style={{ animation: `vault-close ${duration} ease-in-out infinite`, transformOrigin: 'left', background: isHovered ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)' }} />
      </div>
      <div className="relative w-5 h-5 rounded-full border transition-colors duration-300" style={{ borderColor: isHovered ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)' }}>
        <div className="absolute top-1/2 left-1/2 w-0.5 h-2 origin-bottom transition-colors duration-300" style={{ animation: `clock-hand ${duration} linear infinite`, background: isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.4)' }} />
      </div>
    </div>
  );
}

// Burn LP Animation
function BurnLPAnimation({ isHovered }) {
  const duration = isHovered ? '1s' : '2s';
  return (
    <div className="h-16 flex items-center justify-center">
      <style>{`
        @keyframes burn-lp {
          0% { transform: translateY(0) scale(1); opacity: 0.5; }
          100% { transform: translateY(-30px) scale(0.3); opacity: 0; }
        }
      `}</style>
      <div 
        className="w-6 h-6 rounded-full border flex items-center justify-center text-[8px] transition-colors duration-300" 
        style={{ 
          animation: `burn-lp ${duration} ease-out infinite`,
          borderColor: isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)',
          color: isHovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.5)'
        }}
      >LP</div>
    </div>
  );
}

// Yield Farming Animation - Line chart with floating numbers
function YieldAnimation({ isHovered }) {
  const duration = isHovered ? '1.5s' : '3s';
  return (
    <div className="h-16 flex items-end justify-center">
      <svg viewBox="0 0 100 40" className="w-24 h-10">
        <defs>
          <linearGradient id="yield-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={isHovered ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)'} />
            <stop offset="100%" stopColor={isHovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.6)'} />
          </linearGradient>
        </defs>
        <path d="M 5 35 Q 30 30 50 20 T 95 5" fill="none" stroke="url(#yield-line)" strokeWidth={isHovered ? '3' : '2'} strokeLinecap="round">
          <animate attributeName="stroke-dasharray" from="0 200" to="200 0" dur={duration} repeatCount="indefinite" />
        </path>
        <text x="30" y="25" fill={isHovered ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)'} fontSize="6">
          <animate attributeName="opacity" values="0;1;0" dur={duration} repeatCount="indefinite" begin="0.5s" />
          +$1
        </text>
        <text x="55" y="15" fill={isHovered ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)'} fontSize="6">
          <animate attributeName="opacity" values="0;1;0" dur={duration} repeatCount="indefinite" begin="0.75s" />
          +$2
        </text>
        <text x="80" y="8" fill={isHovered ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)'} fontSize="6">
          <animate attributeName="opacity" values="0;1;0" dur={duration} repeatCount="indefinite" begin="1s" />
          +$3
        </text>
      </svg>
    </div>
  );
}

// Buy Tokens Animation - Portfolio bars
function BuyTokensAnimation({ isHovered }) {
  const [widths, setWidths] = useState([80, 60, 50, 35, 20]);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setWidths(prev => {
        const newWidths = [...prev];
        const i = Math.floor(Math.random() * 5);
        const j = Math.floor(Math.random() * 5);
        newWidths[i] = Math.min(90, newWidths[i] + 10);
        newWidths[j] = Math.max(15, newWidths[j] - 10);
        return newWidths.sort((a, b) => b - a);
      });
    }, isHovered ? 800 : 2000);
    return () => clearInterval(interval);
  }, [isHovered]);

  return (
    <div className="h-24 flex flex-col items-start justify-center gap-1 px-4">
      {widths.map((w, i) => (
        <div 
          key={i}
          className="h-1.5 rounded-full transition-all"
          style={{ 
            width: `${w}%`, 
            background: `rgba(255,255,255,${isHovered ? 0.25 + i * 0.12 : 0.15 + i * 0.1})`,
            transitionDuration: isHovered ? '350ms' : '700ms'
          }}
        />
      ))}
    </div>
  );
}

// AAVE Animation - Circular flow
function AaveAnimation({ isHovered }) {
  const duration = isHovered ? '2s' : '4s';
  return (
    <div className="h-16 flex items-center justify-center">
      <svg viewBox="0 0 100 60" className="w-24 h-14">
        <path d="M 20 50 L 50 15 L 80 50 Z" fill="none" stroke={isHovered ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)'} strokeWidth="1" strokeDasharray="4 2" />
        <circle cx="20" cy="50" r="4" fill={isHovered ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)'} />
        <circle cx="50" cy="15" r="4" fill={isHovered ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)'} />
        <circle cx="80" cy="50" r="4" fill={isHovered ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)'} />
        <text x="20" y="60" textAnchor="middle" fill={isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)'} fontSize="6">Supply</text>
        <text x="50" y="10" textAnchor="middle" fill={isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)'} fontSize="6">Earn</text>
        <text x="80" y="60" textAnchor="middle" fill={isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)'} fontSize="6">Reinvest</text>
        <circle r={isHovered ? '3' : '2'} fill="white">
          <animateMotion dur={duration} repeatCount="indefinite" path="M 20 50 L 50 15 L 80 50 L 20 50" />
        </circle>
      </svg>
    </div>
  );
}

// Gold/Silver Animation - Spinning coin
function GoldAnimation({ isHovered }) {
  const duration = isHovered ? '2s' : '4s';
  return (
    <div className="h-16 flex items-center justify-center" style={{ perspective: '600px' }}>
      <style>{`
        @keyframes coin-spin {
          0% { transform: rotateY(0deg); }
          50% { transform: rotateY(180deg); }
          100% { transform: rotateY(360deg); }
        }
      `}</style>
      <div 
        className="relative rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300"
        style={{ 
          width: isHovered ? '44px' : '40px',
          height: isHovered ? '44px' : '40px',
          animation: `coin-spin ${duration} ease-in-out infinite`,
          transformStyle: 'preserve-3d',
          border: `2px solid ${isHovered ? '#E0C060' : '#C9A84C'}`,
          color: isHovered ? '#E0C060' : '#C9A84C',
          boxShadow: isHovered ? '0 0 15px rgba(201,168,76,0.3)' : 'none'
        }}
      >
        <span style={{ backfaceVisibility: 'hidden' }}>Au</span>
      </div>
    </div>
  );
}

// Arbitrage Animation - Two price lines
function ArbitrageAnimation({ isHovered }) {
  const duration = isHovered ? '1.5s' : '3s';
  return (
    <div className="h-16 flex flex-col items-center justify-center gap-2">
      <style>{`
        @keyframes arb-line-top {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(3px); }
        }
        @keyframes arb-line-bottom {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes arb-arrow {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.8; }
        }
      `}</style>
      <div className="flex items-center gap-2">
        <div className="w-12 h-px transition-colors duration-300" style={{ animation: `arb-line-top ${duration} ease-in-out infinite`, background: isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.4)' }} />
        <span className="text-[8px] transition-colors duration-300" style={{ color: isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.4)' }}>$1.02</span>
      </div>
      <div className="text-xs transition-colors duration-300" style={{ animation: `arb-arrow ${duration} ease-in-out infinite`, color: isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)' }}>↕</div>
      <div className="flex items-center gap-2">
        <div className="w-12 h-px transition-colors duration-300" style={{ animation: `arb-line-bottom ${duration} ease-in-out infinite`, background: isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.4)' }} />
        <span className="text-[8px] transition-colors duration-300" style={{ color: isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.4)' }}>$0.98</span>
      </div>
    </div>
  );
}

// Multi-Chain Animation - Broadcast to nodes
function MultichainAnimation({ isHovered }) {
  const duration = isHovered ? '1.5s' : '3s';
  return (
    <div className="h-16 flex items-center justify-center">
      <style>{`
        @keyframes broadcast {
          0% { transform: scaleX(0); opacity: 0; }
          50% { transform: scaleX(1); opacity: 1; }
          100% { transform: scaleX(1); opacity: 0; }
        }
        @keyframes node-flash {
          0%, 40%, 100% { opacity: 0.3; }
          50%, 60% { opacity: 1; }
        }
      `}</style>
      <div className="flex flex-col gap-1">
        {['B', 'E', 'A'].map((c, i) => (
          <div key={c} className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded-full border flex items-center justify-center text-[8px] transition-colors duration-300" 
              style={{ 
                animation: `node-flash ${duration} ease-in-out infinite`, 
                animationDelay: `${i * 0.1}s`,
                borderColor: isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)',
                color: isHovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.5)'
              }}
            >{c}</div>
            <div 
              className="w-8 h-px origin-left transition-colors duration-300" 
              style={{ 
                animation: `broadcast ${duration} ease-out infinite`, 
                animationDelay: `${i * 0.1}s`,
                background: isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.4)'
              }} 
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// Index Animation - Pie chart
function IndexAnimation({ isHovered }) {
  const duration = isHovered ? '4s' : '8s';
  return (
    <div className="h-16 flex items-center justify-center">
      <style>{`
        @keyframes pie-rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <svg viewBox="0 0 40 40" className="w-12 h-12" style={{ animation: `pie-rotate ${duration} linear infinite` }}>
        <circle cx="20" cy="20" r="16" fill="none" stroke={isHovered ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.1)'} strokeWidth="8" />
        <circle cx="20" cy="20" r="16" fill="none" stroke={isHovered ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)'} strokeWidth="8" strokeDasharray="25 75" strokeDashoffset="0" />
        <circle cx="20" cy="20" r="16" fill="none" stroke={isHovered ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.3)'} strokeWidth="8" strokeDasharray="20 80" strokeDashoffset="-25" />
        <circle cx="20" cy="20" r="16" fill="none" stroke={isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.4)'} strokeWidth="8" strokeDasharray="30 70" strokeDashoffset="-45" />
        <circle cx="20" cy="20" r="16" fill="none" stroke={isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.5)'} strokeWidth="8" strokeDasharray="25 75" strokeDashoffset="-75" />
      </svg>
    </div>
  );
}

// Flash Loans Animation - Fast expand/collapse
function FlashAnimation({ isHovered }) {
  const duration = isHovered ? '1.5s' : '3s';
  return (
    <div className="h-16 flex items-center justify-center">
      <style>{`
        @keyframes flash-loan {
          0%, 70%, 100% { transform: scale(0); opacity: 0; }
          10% { transform: scale(0.5); opacity: 1; }
          20% { transform: scale(1.5); opacity: 1; }
          30% { transform: scale(0.5); opacity: 1; }
          40% { transform: scale(0); opacity: 0; }
        }
        @keyframes flash-bolt {
          0%, 15%, 25%, 100% { opacity: 0; }
          20% { opacity: 1; }
        }
      `}</style>
      <div className="relative">
        <div 
          className="rounded-full border-2 transition-all duration-300" 
          style={{ 
            width: isHovered ? '36px' : '32px',
            height: isHovered ? '36px' : '32px',
            animation: `flash-loan ${duration} ease-in-out infinite`,
            borderColor: isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.4)'
          }} 
        />
        <div 
          className="absolute inset-0 flex items-center justify-center text-lg" 
          style={{ 
            animation: `flash-bolt ${duration} ease-in-out infinite`,
            color: isHovered ? '#facc15' : '#eab308'
          }}
        >⚡</div>
      </div>
    </div>
  );
}

// Animation mapping
const animationComponents = {
  'swap': SwapAnimation,
  'bridge': BridgeAnimation,
  'burn': BurnAnimation,
  'vest': VestAnimation,
  'add-lp': AddLPAnimation,
  'lock-lp': LockLPAnimation,
  'burn-lp': BurnLPAnimation,
  'yield': YieldAnimation,
  'buy-tokens': BuyTokensAnimation,
  'aave': AaveAnimation,
  'gold': GoldAnimation,
  'arbitrage': ArbitrageAnimation,
  'multichain': MultichainAnimation,
  'index': IndexAnimation,
  'flash': FlashAnimation,
};

// ============================================================================
// PLUGIN CARD - Mobile Friendly
// ============================================================================
function PluginCard({ plugin, isActive, delay, isMobile }) {
  const [isHovered, setIsHovered] = useState(false);
  const AnimationComponent = animationComponents[plugin.id];
  const categoryLabels = { 'token-ops': 'TOKEN OPS', 'liquidity': 'LIQUIDITY', 'investing': 'INVESTING', 'advanced': 'ADVANCED' };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.4 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`plugin-card p-5 md:p-6 rounded-2xl flex flex-col justify-between transition-all duration-300 cursor-pointer ${!isActive ? 'opacity-20 scale-[0.97] blur-[1px]' : ''}`}
      style={{
        background: isHovered ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)',
        border: `1px solid rgba(255,255,255,${isHovered ? 0.12 : 0.05})`,
        boxShadow: isHovered ? '0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05) inset' : 'none',
        transform: isHovered && isActive ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
        minHeight: isMobile ? '140px' : '160px',
      }}
      data-testid={`plugin-${plugin.id}`}
    >
      {/* Tag */}
      <span 
        className="text-[9px] md:text-[10px] uppercase tracking-wider self-end transition-colors duration-300"
        style={{ color: isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.3)' }}
      >
        {categoryLabels[plugin.category]}
      </span>
      
      {/* Animation - speeds up on hover */}
      <div 
        className="flex-1 flex items-center justify-center transition-transform duration-300 py-2"
        style={{ 
          transform: isHovered ? 'scale(1.1)' : 'scale(1)',
        }}
      >
        {AnimationComponent && <AnimationComponent isHovered={isHovered} />}
      </div>
      
      {/* Info */}
      <div>
        <h4 
          className="text-sm md:text-base font-semibold transition-colors duration-300"
          style={{ color: isHovered ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.9)' }}
        >
          {plugin.name}
        </h4>
        <p 
          className="text-[11px] md:text-xs mt-1 leading-relaxed transition-colors duration-300 line-clamp-2"
          style={{ color: isHovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.4)' }}
        >
          {plugin.desc}
        </p>
        
        {/* API tooltip on hover - hidden on mobile */}
        {!isMobile && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: isHovered ? 1 : 0, height: isHovered ? 'auto' : 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div 
              className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2"
            >
              <span className="text-[10px] font-mono text-white/40">
                api.aibl.dev/v1/{plugin.id}
              </span>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-white/50">
                POST
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================================================
// MAIN EXPORT
// ============================================================================
export default function PluginsSection() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [isMobile, setIsMobile] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section 
      id="plugins"
      className="py-[80px] md:py-[120px] overflow-hidden"
      style={{ background: '#0A0A0A' }}
      data-testid="plugins-section"
    >
      <div className="max-w-[1100px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-xs uppercase tracking-[0.08em] font-semibold text-white/70 px-4 py-1.5 rounded-full mb-4 md:mb-6"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}
          >
            Plugins
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[28px] md:text-[40px] font-extrabold text-white tracking-[-0.03em]"
          >
            Give your agent superpowers.
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-sm md:text-base text-gray-400 mt-2 md:mt-3"
          >
            20+ onchain actions. One API call each.
          </motion.p>
        </div>

        {/* Live Feed - Hidden on mobile for cleaner look */}
        <div className="hidden md:block">
          <AgentFeed />
        </div>

        {/* Category Tabs - Scrollable on mobile */}
        <div className="flex overflow-x-auto pb-2 md:pb-0 md:flex-wrap justify-start md:justify-center gap-2 mb-6 md:mb-10 scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`text-[12px] md:text-[13px] px-3 md:px-4 py-1.5 md:py-2 rounded-full transition-all whitespace-nowrap flex-shrink-0 ${
                activeCategory === cat.id
                  ? 'bg-white/5 border border-white/10 text-white'
                  : 'text-gray-500 hover:text-white'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Bento Grid - Responsive */}
        <div 
          ref={ref}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
        >
          {plugins.map((plugin, i) => (
            <PluginCard
              key={plugin.id}
              plugin={plugin}
              isActive={activeCategory === 'all' || activeCategory === plugin.category}
              delay={i * 0.03}
              isMobile={isMobile}
            />
          ))}
        </div>

        {/* Footer - Shipping More */}
        <div className="text-center mt-12 md:mt-16 mb-16 md:mb-24">
          <div 
            className="inline-block px-8 md:px-12 py-6 md:py-8 rounded-2xl"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <p className="text-sm md:text-base text-gray-400 mb-4 md:mb-5 font-medium">And we're shipping more every week.</p>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {['NFT Minting', 'DAO Voting', 'Insurance', 'Options'].map((item) => (
                <span 
                  key={item}
                  className="text-[10px] md:text-[11px] text-gray-500 px-3 md:px-4 py-1.5 rounded-full"
                  style={{ border: '1px dashed rgba(255,255,255,0.1)' }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
